import { z } from 'zod';
import { baseComponentSchema, buttonSchema } from '../component-base.schemas';

// Countdown Timer component schemas
export const countdownTimerBackgroundImageSchema = z
  .object({
    enabled: z.boolean().default(false),
    url: z.string().url('Invalid image URL').optional(),
    cloudfrontUrl: z.string().url('Invalid CloudFront URL').optional(),
    alt: z.string().max(200, 'Alt text too long').optional(),
    position: z
      .enum([
        'center',
        'top',
        'bottom',
        'left',
        'right',
        'top-left',
        'top-right',
        'bottom-left',
        'bottom-right',
      ])
      .default('center')
      .optional(),
    size: z.enum(['cover', 'contain', 'auto', '100%']).default('cover').optional(),
    repeat: z.enum(['no-repeat', 'repeat', 'repeat-x', 'repeat-y']).default('no-repeat').optional(),
    parallax: z.boolean().default(false).optional(),
    overlay: z
      .object({
        enabled: z.boolean().default(false),
        color: z
          .string()
          .regex(/^#[0-9A-Fa-f]{6}$/)
          .default('#000000')
          .optional(),
        opacity: z.number().min(0).max(1).default(0.5).optional(),
      })
      .optional(),
  })
  .optional();

export const countdownTimerCustomColorsSchema = z
  .object({
    background: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    text: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    headline: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    subheadline: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    number: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    label: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    buttonBg: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    buttonText: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
  })
  .optional();

export const countdownTimerDataSchema = z.object({
  // Headlines
  headline: z.string().max(200, 'Headline must be less than 200 characters').optional(),
  subheadline: z.string().max(300, 'Subheadline must be less than 300 characters').optional(),
  showHeadline: z.boolean().default(true),
  showSubheadline: z.boolean().default(true),
  headlineAlignment: z.enum(['left', 'center', 'right']).default('center'),
  headlineSize: z.enum(['small', 'medium', 'large']).default('medium'),

  // Background
  backgroundImage: countdownTimerBackgroundImageSchema,
  backgroundColor: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/)
    .optional(),

  // Countdown Timer
  targetDate: z.string().datetime('Invalid target date format'),
  displayFormat: z.enum(['full', 'compact', 'minimal']).default('full'),
  showLabels: z.boolean().default(true),
  showSeparators: z.boolean().default(true),
  units: z.object({
    showDays: z.boolean().default(true),
    showHours: z.boolean().default(true),
    showMinutes: z.boolean().default(true),
    showSeconds: z.boolean().default(true),
  }),

  // Expiration
  expiredMessage: z
    .string()
    .max(200, 'Expired message must be less than 200 characters')
    .default('This offer has ended'),
  hideWhenExpired: z.boolean().default(false),

  // Layout & Styling
  layout: z.enum(['horizontal', 'vertical', 'grid']).default('horizontal'),
  alignment: z.enum(['left', 'center', 'right']).default('center'),
  size: z.enum(['small', 'medium', 'large']).default('medium'),
  numberStyle: z.enum(['default', 'bold', 'card', 'gradient']).default('default'),
  separatorStyle: z.enum(['colon', 'slash', 'dot', 'none']).default('colon'),

  // Colors
  textColor: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/)
    .optional(),
  headlineColor: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/)
    .optional(),
  subheadlineColor: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/)
    .optional(),
  numberColor: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/)
    .optional(),
  labelColor: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/)
    .optional(),

  customColors: countdownTimerCustomColorsSchema,

  // Optional call-to-action buttons
  buttons: z.array(buttonSchema).optional(),
});

export const countdownTimerComponentSchema = baseComponentSchema.extend({
  type: z.literal('countdown-timer'),
  data: countdownTimerDataSchema,
});

// Type exports
export type CountdownTimerData = z.infer<typeof countdownTimerDataSchema>;
export type CountdownTimerComponent = z.infer<typeof countdownTimerComponentSchema>;
